(() => {
  if (window.RDYouTubeReelsLoaded) return;
  window.RDYouTubeReelsLoaded = true;

  const extractYouTubeId = (value = '') => {
    const raw = String(value).trim();

    if (!raw) return '';

    try {
      const url = new URL(raw);

      const host = url.hostname
        .replace(/^www\./, '')
        .replace(/^m\./, '');

      /* youtu.be/VIDEO_ID */
      if (host === 'youtu.be') {
        return url.pathname
          .split('/')
          .filter(Boolean)[0] || '';
      }

      if (
        host === 'youtube.com' ||
        host.endsWith('.youtube.com')
      ) {
        const parts = url.pathname
          .split('/')
          .filter(Boolean);

        /* youtube.com/watch?v=VIDEO_ID */
        if (url.pathname === '/watch') {
          return url.searchParams.get('v') || '';
        }

        /* youtube.com/shorts/VIDEO_ID */
        const shortsIndex = parts.indexOf('shorts');

        if (
          shortsIndex !== -1 &&
          parts[shortsIndex + 1]
        ) {
          return parts[shortsIndex + 1];
        }

        /* youtube.com/embed/VIDEO_ID */
        const embedIndex = parts.indexOf('embed');

        if (
          embedIndex !== -1 &&
          parts[embedIndex + 1]
        ) {
          return parts[embedIndex + 1];
        }

        /* youtube.com/live/VIDEO_ID */
        const liveIndex = parts.indexOf('live');

        if (
          liveIndex !== -1 &&
          parts[liveIndex + 1]
        ) {
          return parts[liveIndex + 1];
        }
      }
    } catch (error) {
      /* Fall through to regex */
    }

    const match = raw.match(
      /(?:youtu\.be\/|youtube\.com\/(?:shorts\/|embed\/|live\/)|[?&]v=)([A-Za-z0-9_-]{6,})/
    );

    return match ? match[1] : '';
  };


  class RDYouTubeReels {
    constructor(root) {
      this.root = root;

      this.track = root.querySelector(
        '[data-rd-reels-track]'
      );

      this.prev = root.querySelector(
        '[data-rd-reels-prev]'
      );

      this.next = root.querySelector(
        '[data-rd-reels-next]'
      );

      this.modal = root.querySelector(
        '[data-rd-reels-modal]'
      );

      this.video = root.querySelector(
        '[data-rd-reels-video]'
      );

      this.lastTrigger = null;

      this.onTrackScroll =
        this.updateArrows.bind(this);

      this.onKeyDown =
        this.handleKeyDown.bind(this);

      this.init();
    }


    init() {
      this.prepareAutoThumbnails();
      this.prepareAdminWarnings();
      this.bindEvents();

      requestAnimationFrame(() => {
        this.updateArrows();
      });
    }


    prepareAutoThumbnails() {
      this.root
        .querySelectorAll('[data-rd-auto-thumb]')
        .forEach((holder) => {

          const id = extractYouTubeId(
            holder.dataset.videoUrl
          );

          if (!id) return;

          const img = document.createElement('img');

          img.src =
            `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

          img.alt = '';
          img.loading = 'lazy';
          img.width = 480;
          img.height = 360;

          holder.appendChild(img);
        });
    }


    prepareAdminWarnings() {
      this.root
        .querySelectorAll('[data-rd-url-warning]')
        .forEach((warning) => {

          const id = extractYouTubeId(
            warning.dataset.videoUrl
          );

          if (id) {
            warning.remove();
            return;
          }

          warning.textContent =
            'Invalid YouTube link — paste the full Shorts or YouTube URL.';
        });
    }


    bindEvents() {
      this.prev?.addEventListener('click', () => {
        this.scroll(-1);
      });

      this.next?.addEventListener('click', () => {
        this.scroll(1);
      });


      this.track?.addEventListener(
        'scroll',
        this.onTrackScroll,
        { passive: true }
      );


      this.root.addEventListener('click', (event) => {

        const trigger = event.target.closest(
          '[data-rd-reel-open]'
        );

        if (trigger) {
          this.openVideo(trigger);
          return;
        }


        const close = event.target.closest(
          '[data-rd-reels-close]'
        );

        if (close) {
          this.closeVideo();
        }
      });


      document.addEventListener(
        'keydown',
        this.onKeyDown
      );


      window.addEventListener(
        'resize',
        () => this.updateArrows(),
        { passive: true }
      );
    }


    getScrollAmount() {
      if (!this.track) return 300;

      const card = this.track.querySelector(
        '.rd-reels__card'
      );

      if (!card) return 300;

      const styles =
        window.getComputedStyle(this.track);

      const gap =
        parseFloat(styles.columnGap) ||
        parseFloat(styles.gap) ||
        0;

      return card.getBoundingClientRect().width + gap;
    }


    scroll(direction) {
      if (!this.track) return;

      this.track.scrollBy({
        left:
          this.getScrollAmount() *
          direction,
        behavior: 'smooth'
      });
    }


    updateArrows() {
      if (!this.track) return;

      const max =
        this.track.scrollWidth -
        this.track.clientWidth;

      if (this.prev) {
        this.prev.disabled =
          this.track.scrollLeft <= 3;
      }

      if (this.next) {
        this.next.disabled =
          this.track.scrollLeft >= max - 3;
      }
    }


    openVideo(trigger) {
      if (!this.modal || !this.video) return;

      const id =
        trigger.dataset.videoId ||
        extractYouTubeId(
          trigger.dataset.videoUrl
        );

      if (!id) return;

      this.lastTrigger = trigger;

      const iframe =
        document.createElement('iframe');

      iframe.src =
        `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&playsinline=1&rel=0`;

      iframe.title = 'YouTube video';
      iframe.loading = 'eager';

      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

      iframe.allowFullscreen = true;

      this.video.replaceChildren(iframe);

      this.modal.hidden = false;

      document.documentElement
        .classList.add('rd-reels-modal-open');

      document.body
        .classList.add('rd-reels-modal-open');

      const closeButton =
        this.modal.querySelector(
          '.rd-reels-modal__close'
        );

      closeButton?.focus({
        preventScroll: true
      });
    }


    closeVideo() {
      if (!this.modal || !this.video) return;

      this.modal.hidden = true;

      this.video.replaceChildren();

      document.documentElement
        .classList.remove('rd-reels-modal-open');

      document.body
        .classList.remove('rd-reels-modal-open');

      this.lastTrigger?.focus({
        preventScroll: true
      });

      this.lastTrigger = null;
    }


    handleKeyDown(event) {
      if (
        event.key === 'Escape' &&
        this.modal &&
        !this.modal.hidden
      ) {
        this.closeVideo();
      }
    }
  }


  const init = (scope = document) => {
    scope
      .querySelectorAll('[data-rd-reels]')
      .forEach((root) => {

        if (root.dataset.rdReelsReady) return;

        root.dataset.rdReelsReady = 'true';

        new RDYouTubeReels(root);
      });
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init()
    );
  } else {
    init();
  }


  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(event.target);
    }
  );
})(); 